import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Card, CardContent } from "./ui/card";

/**
 * ResponsiveTable - A table component that shows as cards on mobile and table on desktop
 *
 * @param {Array} columns - Array of column definitions: [{ key, label, render, mobileLabel }]
 * @param {Array} data - Array of data objects
 * @param {Function} onRowClick - Optional click handler for rows
 * @param {Boolean} loading - Loading state
 * @param {String} emptyMessage - Message to show when no data
 * @param {Number} colSpan - Number of columns for empty/loading state
 */
const ResponsiveTable = ({
  columns,
  data,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
  loadingMessage = "Loading...",
}) => {
  if (loading) {
    return (
      <div className="py-8">
        {/* Desktop Loading */}
        <div className="hidden md:block">
          <Table>
            <TableHeader className="bg-gray-50/50">
              <TableRow className="border-0">
                {columns.map((col, idx) => (
                  <TableHead
                    key={idx}
                    className="font-semibold text-gray-700 border-0"
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-0">
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-8 border-0"
                >
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Mobile Loading */}
        <div className="md:hidden flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="py-8">
        {/* Desktop Empty State */}
        <div className="hidden md:block">
          <Table>
            <TableHeader className="bg-gray-50/50">
              <TableRow className="border-0">
                {columns.map((col, idx) => (
                  <TableHead
                    key={idx}
                    className="font-semibold text-gray-700 border-0"
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="border-0">
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-8 text-gray-500 border-0"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Mobile Empty State */}
        <div className="md:hidden text-center py-12 text-gray-500">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Table>
          <TableHeader className="bg-gray-50/50">
            <TableRow className="border-0">
              {columns.map((col, idx) => (
                <TableHead
                  key={idx}
                  className="font-semibold text-gray-700 border-0"
                >
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className="border-0 hover:bg-gray-50/30 transition-colors cursor-pointer"
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((col, colIndex) => (
                  <TableCell key={colIndex} className="py-4 border-0 text-gray-900">
                    {col.render ? col.render(row) : row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 px-4 py-3">
        {data.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 overflow-hidden"
            onClick={() => onRowClick && onRowClick(row)}
          >
            <div className="p-6 space-y-5">
              {columns.map((col, colIndex) => (
                <div
                  key={colIndex}
                  className="flex justify-between items-start gap-6"
                >
                  <div className="text-sm font-semibold text-gray-500 min-w-[85px] flex-shrink-0">
                    {col.mobileLabel || col.label}
                  </div>
                  <div className="text-sm text-gray-900 text-right flex-1 break-words font-medium">
                    {col.render ? col.render(row) : row[col.key]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ResponsiveTable;
